import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faPause,
  faUndo,
  faCog,
  faCheckCircle,
  faSpinner,
  faClock,
  faRobot,
  faSearch,
  faQuoteLeft,
  faBrain,
  faLightbulb,
  faExpand,
  faTimes,
  faDownload,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { getAllClients } from '../../services/api';

interface PipelineStage {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  duration?: number;
  data?: any;
  icon: any;
  color: string;
}

interface PipelineVisualizerProps {}

interface Client {
  id: string;
  name: string;
  website_url?: string;
}

const PipelineVisualizer: React.FC<PipelineVisualizerProps> = () => {
  const [query, setQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [clients, setClients] = useState<Client[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [currentStage, setCurrentStage] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const [finalResult, setFinalResult] = useState<string>('');
  const [showResultModal, setShowResultModal] = useState(false);
  const [pipelineData, setPipelineData] = useState<any>({});
  const websocketRef = useRef<WebSocket | null>(null);

  const initialStages: PipelineStage[] = [
    {
      id: 'initialization',
      name: 'Initialization',
      description: 'Starting RAG pipeline and setting up context',
      status: 'pending',
      icon: faCog,
      color: '#3B82F6'
    },
    {
      id: 'query_classification',
      name: 'Query Classification',
      description: 'Analyzing query type and structure',
      status: 'pending',
      icon: faBrain,
      color: '#8B5CF6'
    },
    {
      id: 'intent_detection',
      name: 'Intent Detection',
      description: 'Detecting user intent using pattern matching and LLM',
      status: 'pending',
      icon: faLightbulb,
      color: '#F59E0B'
    },
    {
      id: 'context_gathering',
      name: 'Context Gathering',
      description: 'Retrieving user preferences and conversation history',
      status: 'pending',
      icon: faSearch,
      color: '#10B981'
    },
    {
      id: 'embedding_generation',
      name: 'Embedding Generation',
      description: 'Converting query to vector representation',
      status: 'pending',
      icon: faRobot,
      color: '#EF4444'
    },
    {
      id: 'vector_search',
      name: 'Vector Search',
      description: 'Searching Pinecone for relevant content',
      status: 'pending',
      icon: faSearch,
      color: '#6366F1'
    },
    {
      id: 'citation_retrieval',
      name: 'Citation Retrieval',
      description: 'Finding and processing citation sources',
      status: 'pending',
      icon: faQuoteLeft,
      color: '#EC4899'
    },
    {
      id: 'prompt_construction',
      name: 'Dynamic Prompt Construction',
      description: 'Building context-aware system prompt',
      status: 'pending',
      icon: faCog,
      color: '#14B8A6'
    },
    {
      id: 'llm_generation',
      name: 'LLM Generation',
      description: 'Streaming response from language model',
      status: 'pending',
      icon: faRobot,
      color: '#F97316'
    }
  ];

  useEffect(() => {
    setStages(initialStages);
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const data = await getAllClients();
      if (data.success) {
        setClients(data.clients);
        if (data.clients.length > 0) {
          setSelectedClient(data.clients[0].id);
        }
      } else {
        setClients(data); // Handle if the response is directly the clients array
        if (data.length > 0) {
          setSelectedClient(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    }
  };

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const updateStage = (stageId: string, updates: Partial<PipelineStage>) => {
    setStages(prev => prev.map(stage => 
      stage.id === stageId ? { ...stage, ...updates } : stage
    ));
  };

  const startPipeline = async () => {
    if (!query.trim()) {
      toast.error('Please enter a query to test');
      return;
    }

    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    setIsRunning(true);
    setCurrentStage(0);
    setLogs([]);
    setFinalResult('');
    setPipelineData({});

    // Reset all stages
    setStages(prev => prev.map(stage => ({ ...stage, status: 'pending' as const, duration: undefined, data: undefined })));

    addLog('🚀 Starting RAG Pipeline Analysis');
    addLog(`📝 Query: "${query}"`);
    addLog(`👤 Client: ${clients.find(c => c.id === selectedClient)?.name || selectedClient}`);

    try {
      // Connect to WebSocket for real-time updates
      const wsUrl = `ws://localhost:8000/api/chat/pipeline-stream`;
      websocketRef.current = new WebSocket(wsUrl);

      websocketRef.current.onopen = () => {
        addLog('🔗 Connected to pipeline stream');
        // Send the query to start pipeline
        websocketRef.current?.send(JSON.stringify({
          query: query,
          client_id: selectedClient,
          visualize: true
        }));
      };

      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handlePipelineUpdate(data);
        } catch (e) {
          addLog(`📡 Raw message: ${event.data}`);
        }
      };

      websocketRef.current.onerror = (error) => {
        addLog('❌ WebSocket error occurred');
        console.error('WebSocket error:', error);
      };

      websocketRef.current.onclose = () => {
        addLog('🔌 Pipeline stream disconnected');
        setIsRunning(false);
      };

    } catch (error) {
      addLog(`❌ Error starting pipeline: ${error}`);
      setIsRunning(false);
    }
  };

  const handlePipelineUpdate = (data: any) => {
    if (data.type === 'pipeline_stage') {
      const { stage, status, duration, metadata } = data;
      
      addLog(`⚡ ${stage}: ${status} ${duration ? `(${duration}ms)` : ''}`);
      
      // Update stage status
      updateStage(stage, {
        status: status,
        duration: duration,
        data: metadata
      });

      // Move current stage indicator
      const stageIndex = stages.findIndex(s => s.id === stage);
      if (stageIndex >= 0) {
        setCurrentStage(stageIndex);
      }

      // Store pipeline data for visualization
      setPipelineData((prev: any) => ({
        ...prev,
        [stage]: { status, duration, metadata }
      }));
    } else if (data.type === 'pipeline_complete') {
      addLog('✅ Pipeline completed successfully');
      setFinalResult(data.content || 'Pipeline completed');
      setIsRunning(false);
    } else if (data.type === 'pipeline_error') {
      addLog(`❌ Pipeline error: ${data.error}`);
      setIsRunning(false);
    } else if (data.type === 'text_chunk') {
      // Accumulate response text
      setFinalResult(prev => prev + data.content);
    }
  };

  const stopPipeline = () => {
    if (websocketRef.current) {
      websocketRef.current.close();
    }
    setIsRunning(false);
    addLog('⏹️ Pipeline stopped by user');
  };

  const resetPipeline = () => {
    setStages(initialStages);
    setCurrentStage(0);
    setLogs([]);
    setFinalResult('');
    setPipelineData({});
    setQuery('');
    addLog('🔄 Pipeline reset');
  };

  const downloadLogs = () => {
    const logContent = logs.join('\n');
    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pipeline-logs-${new Date().toISOString()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyLogs = () => {
    navigator.clipboard.writeText(logs.join('\n'));
    toast.success('Logs copied to clipboard');
  };

  const getStageIcon = (stage: PipelineStage) => {
    if (stage.status === 'processing') {
      return <FontAwesomeIcon icon={faSpinner} className="animate-spin" />;
    } else if (stage.status === 'completed') {
      return <FontAwesomeIcon icon={faCheckCircle} />;
    } else if (stage.status === 'error') {
      return <FontAwesomeIcon icon={faTimes} />;
    } else {
      return <FontAwesomeIcon icon={stage.icon} />;
    }
  };

  const getStageProgress = () => {
    const completedStages = stages.filter(s => s.status === 'completed').length;
    return (completedStages / stages.length) * 100;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            🧠 RAG Pipeline Visualizer
          </h1>
          <p className="text-slate-300 text-lg">
            Watch your RAG system work in real-time with sophisticated pipeline visualization
          </p>
        </div>

        {/* Control Panel */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20">
          <div className="flex flex-col gap-4">
            {/* Client Selection */}
            <div>
              <label className="block text-white font-medium mb-2">Select Client</label>
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isRunning}
              >
                <option value="">Choose a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id} className="bg-slate-800">
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Query Input and Controls */}
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter your query to test the RAG pipeline..."
                  className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isRunning}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isRunning && query.trim() && selectedClient) {
                      startPipeline();
                    }
                  }}
                />
              </div>
              <div className="flex gap-3">
                {!isRunning ? (
                <button
                  onClick={startPipeline}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faPlay} />
                  Start Pipeline
                </button>
              ) : (
                <button
                  onClick={stopPipeline}
                  className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faPause} />
                  Stop
                </button>
              )}
                <button
                  onClick={resetPipeline}
                  className="px-6 py-3 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faUndo} />
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm text-white/70 mb-2">
              <span>Pipeline Progress</span>
              <span>{Math.round(getStageProgress())}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${getStageProgress()}%` }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Pipeline Stages */}
          <div className="lg:col-span-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <FontAwesomeIcon icon={faCog} className="text-blue-400" />
                Pipeline Stages
              </h2>

              <div className="space-y-4">
                {stages.map((stage, index) => (
                  <div
                    key={stage.id}
                    className={`relative p-4 rounded-lg border transition-all duration-300 ${
                      stage.status === 'completed' 
                        ? 'bg-green-500/20 border-green-400/50'
                        : stage.status === 'processing'
                        ? 'bg-blue-500/20 border-blue-400/50 animate-pulse'
                        : stage.status === 'error'
                        ? 'bg-red-500/20 border-red-400/50'
                        : 'bg-white/5 border-white/20'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                          stage.status === 'completed' ? 'bg-green-500' :
                          stage.status === 'processing' ? 'bg-blue-500' :
                          stage.status === 'error' ? 'bg-red-500' :
                          'bg-slate-600'
                        }`}
                        style={{ backgroundColor: stage.status === 'pending' ? stage.color : undefined }}
                      >
                        {getStageIcon(stage)}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white">
                          {stage.name}
                        </h3>
                        <p className="text-slate-300 text-sm">
                          {stage.description}
                        </p>
                        {stage.duration && (
                          <p className="text-blue-400 text-xs mt-1">
                            Duration: {stage.duration}ms
                          </p>
                        )}
                      </div>

                      {stage.data && (
                        <button
                          onClick={() => {
                            toast.info('Stage data: ' + JSON.stringify(stage.data, null, 2));
                          }}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          <FontAwesomeIcon icon={faExpand} />
                        </button>
                      )}
                    </div>

                    {/* Progress line */}
                    {index < stages.length - 1 && (
                      <div className="absolute left-10 top-16 w-0.5 h-6 bg-white/20"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Logs and Results */}
          <div className="space-y-6">
            {/* Real-time Logs */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <FontAwesomeIcon icon={faClock} className="text-green-400" />
                  Live Logs
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={copyLogs}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                    title="Copy logs"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
                  <button
                    onClick={downloadLogs}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                    title="Download logs"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                </div>
              </div>
              
              <div className="bg-black/30 rounded-lg p-4 h-64 overflow-y-auto font-mono text-sm">
                {logs.length === 0 ? (
                  <p className="text-slate-400">No logs yet. Start a pipeline to see real-time updates.</p>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} className="text-green-400 mb-1">
                      {log}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Final Result */}
            {finalResult && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-400" />
                    Result
                  </h3>
                  <button
                    onClick={() => setShowResultModal(true)}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <FontAwesomeIcon icon={faExpand} />
                  </button>
                </div>
                
                <div className="bg-black/30 rounded-lg p-4 max-h-32 overflow-y-auto text-sm text-white">
                  {finalResult.substring(0, 200)}
                  {finalResult.length > 200 && '...'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Result Modal */}
        {showResultModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">
                  Pipeline Result
                </h3>
                <button
                  onClick={() => setShowResultModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="p-6">
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm whitespace-pre-wrap">
                  {finalResult}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineVisualizer;